import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, Group, Loader, Segmented, Stack, Text, Title } from '@grovus/design-system'
import type { Bounds, CreativesResponse, Dimension } from '../creatives/contract'
import { matchesEcho, periodKey, type Period } from '../lib/period'
import { thresholdsOf } from '../creatives/confidence'
import { CreativesRequestError, REQUEST_TIMEOUT_MS } from '../creatives/api'
import { createListReads, type ListReads } from '../creatives/reads'
import { EmptyState } from '../creatives/EmptyState'
import { NoDeliveryLine } from '../creatives/NoDeliveryLine'
import { ScopeNotices } from '../creatives/ScopeNotices'
import { errorCopy, SHELL_FIXABLE } from '../creatives/refusal'
import { labelOf } from '../creatives/view'
import { launched, partialScopeOf, UNASSIGNED } from '../creatives/partition'
import { DICT, DICTIONARY_COPY, type Lang } from '../i18n'
import type { Axis, BucketRow } from './pool'
import { bucketTotals, orderBuckets, pool, poolAudience } from './pool'
import { footnoteText, footnoteVariables } from './footnote'
import { HypothesesTable } from './HypothesesTable'
// Explicit extension: the pure module `footnote.ts` and this component differ only by
// case, and case-insensitive module resolution would pick the wrong one.
import { Footnote } from './Footnote.tsx'

/** Rounds fetched before a dataset that keeps changing under them is shown as an error. */
const ROUND_ATTEMPTS = 3
/** The pause between two rounds that read different datasets. */
const ROUND_PAUSE_MS = 2_000

/**
 * The hypotheses screen: the comparison that pools creatives into buckets across three
 * groupings, ranked where the pooled sample supports a ratio, with every row opening the
 * creatives inside it.
 *
 * It owns everything below the shell's header: fetching, the load and error states, the
 * axis control, the table and the footnote. It renders nothing above them and knows
 * nothing about routes, the sidebar or persistence. It reads the same scope the creatives
 * screen reads, and `accountIds: []` makes no request and renders the one-line
 * no-selection state, the creatives screen's rule, so an empty selection can never widen
 * into everything.
 */
export interface HypothesesScreenProps {
  lang: Lang
  /** The active company. Every request names it, and a response is held under it. */
  companyId: string
  period: Period
  /** `null` is the company's default scope; `[]` makes no request at all. */
  accountIds: string[] | null
  /** Controlled: the shell holds it so saved views and the way back land where they left. */
  axis: Axis
  onAxisChange: (axis: Axis) => void
  /** Every row is a way in. `id` is a dimension id, or the unassigned sentinel. */
  onOpenBucket: (axis: Axis, id: string) => void
  /**
   * The shell's reporting scope key, held beside the response it asked for and handed
   * back with every count. Opaque here: the screen never reads it.
   */
  scope?: string
  /** The creatives screen's shell callbacks, with the same meaning. */
  onBounds?: (report: { scope: string; bounds: Bounds | null }) => void
  onOldContract?: () => void
  onPeriodRefused?: (report: { scope: string }) => void
  /** Opens the period control; passed only while the shell holds bounds for the selection. */
  onOpenPeriod?: () => void
  /**
   * The reader changed the account selection and the request for it has not been sent yet.
   * The retained figures still answer the previous selection, so nothing read off them may
   * be offered as this one's.
   */
  settling?: boolean
  rangeRefused?: boolean
  /**
   * The guest view's re-read moved the auth generation (GRO-83): the requests it aborted
   * are issued again with the same selections. Constant for a member.
   */
  reloadKey?: number
  /** Opens the hypothesis dictionary (GRO-157). Absent for a guest, who has no way there. */
  onManageDictionary?: () => void
  /** Injected in tests; the real screen uses the global. */
  fetchImpl?: typeof fetch
  /**
   * The shell's shared list reads. The base request asks what the sidebar badge asks, so
   * the two share one read, and a round read before the screen was unmounted paints first
   * when it mounts again under the same scope; the round still runs and replaces it.
   */
  reads?: ListReads
}

interface Load {
  status: 'loading' | 'ready' | 'error'
  message?: string
  code?: string
  accounts?: string[]
}

function dimensionList(
  axis: Axis,
  dimensions: { hypotheses: Dimension[]; techniques: Dimension[]; messages: Dimension[]; offers: Dimension[]; audiences: Dimension[] },
): Dimension[] {
  return axis === 'hypothesis'
    ? dimensions.hypotheses
    : axis === 'technique'
      ? dimensions.techniques
      : dimensions.audiences
}

export function HypothesesScreen({
  lang,
  companyId,
  period,
  accountIds,
  axis,
  onAxisChange,
  onOpenBucket,
  scope = '',
  onBounds,
  onOldContract,
  onPeriodRefused,
  onOpenPeriod,
  settling = false,
  rangeRefused = false,
  reloadKey = 0,
  onManageDictionary,
  fetchImpl,
  reads,
}: HypothesesScreenProps) {
  const t = DICT[lang]
  const own = useRef<ListReads | null>(null)
  const store = reads ?? (own.current ??= createListReads())
  const windowKey = periodKey(period)
  const shell = useRef({ onBounds, onOldContract, onPeriodRefused })
  shell.current = { onBounds, onOldContract, onPeriodRefused }
  const [load, setLoad] = useState<Load>({ status: 'loading' })
  // The retained responses are held under the company they answer, the creatives screen's
  // rule: a refetch for another period keeps them up, a company change drops them.
  const [held, setHeld] = useState<{ companyId: string; base: CreativesResponse; scoped: CreativesResponse[] } | null>(null)
  const base = held !== null && held.companyId === companyId ? held.base : null
  const scoped = useMemo(
    () => (held !== null && held.companyId === companyId ? held.scoped : []),
    [held, companyId],
  )
  const [attempt, setAttempt] = useState(0)
  // The request the displayed base actually answers. While a refetch runs the screen
  // keeps the previous rows on purpose, and their drill buttons must not open a bucket
  // under a scope those rows do not describe: a comparison narrowed from all accounts to
  // one, clicked before the new response lands, would send the reader to a creatives
  // list where the clicked bucket has no members.
  const [servedFor, setServedFor] = useState<{ windowKey: string; accountKey: string | null; scope: string; axis: Axis } | null>(null)

  const noSelection = accountIds !== null && accountIds.length === 0
  const accountKey = accountIds === null ? null : accountIds.join(',')

  // The base request, and on the audience axis one scoped request per audience, issued
  // after the base names them and in parallel among themselves. Memoised in screen state
  // while the period, the scope and the axis hold: a change to any of them refetches, and
  // so does a retry, which re-issues the base request and every scoped request from a
  // cleared memo. Any scoped request failing fails the axis: a comparison missing one of
  // its buckets is not a comparison - the share column would still sum to 100 over the
  // buckets present and the footer would not reconcile with the rows, with nothing on
  // screen accounting for the difference.
  useEffect(() => {
    if (noSelection) {
      setLoad({ status: 'ready' })
      setHeld(null)
      setServedFor(null)
      return
    }
    let current = true
    // The creatives screen's rule: a request the screen no longer wants is cancelled. One
    // controller covers the base, every audience request and the pause between attempts.
    const controller = new AbortController()
    const doFetch = fetchImpl ?? fetch
    const tag = { scope, reloadKey }
    const baseParams = { companyId, period, accountIds, audienceId: null }
    // A round this pair was last answered with goes up before the new round: the base, and
    // on the audience axis every cut read at the base's instant. One cut missing seeds
    // nothing, because the partition is never shown with a bucket short. A seeded round
    // answers the current request, so the screen is ready with it: a loading line in the
    // same render would paint above the table and drop out a frame later, moving it.
    let seeded = false
    const keptBase = store.retained(baseParams, tag)
    if (keptBase !== null && matchesEcho({ period, compare: false }, keptBase)) {
      const keptCuts = axis === 'audience'
        ? keptBase.dimensions.audiences.map((audience) =>
          store.retained({ ...baseParams, audienceId: audience.id, asOf: keptBase.asOf }, tag))
        : []
      if (keptCuts.every((cut) => cut !== null)) {
        setHeld({ companyId, base: keptBase, scoped: keptCuts })
        setServedFor({ windowKey, accountKey, scope, axis })
        seeded = true
      }
    }
    setLoad({ status: seeded ? 'ready' : 'loading' })
    // One round: the base, then one request per audience on the audience axis. The base
    // fixes the round's window, and a bucket that answers another one - a preset resolved
    // on the far side of midnight - discards the round, which is fetched once more. Two
    // consecutive rounds of a few hundred milliseconds cannot both straddle a midnight.
    // The base also fixes the round's dataset: every audience request carries the base's
    // `as_of`, and a response read at another revision, or evaluated at another instant,
    // means ingestion committed during the round. That round is fetched again, up to
    // ROUND_ATTEMPTS in all, ROUND_PAUSE_MS apart, and the last mismatch is an error: the
    // comparison is never pooled across two datasets. A server that echoes no revision is
    // accepted without the comparison.
    const round = async (retries: number, attempt = 1): Promise<void> => {
      // Each attempt has its own timeout, as each request had before it shared a signal.
      const signal = AbortSignal.any([controller.signal, AbortSignal.timeout(REQUEST_TIMEOUT_MS)])
      const unscoped = await store.read(baseParams, tag, doFetch, signal)
      if (!current) return
      // The creatives screen's rule: the contract first, then the echo. This screen never
      // asks for a comparison.
      const answers = (response: CreativesResponse) => matchesEcho({ period, compare: false }, response)
      const old = unscoped.contract === 'old'
      if (old) shell.current.onOldContract?.()
      if (!answers(unscoped)) {
        if (old) return
        throw new Error(`${windowKey}: the response answers another window`)
      }
      if (!old) shell.current.onBounds?.({ scope, bounds: unscoped.bounds })
      if (axis !== 'audience') {
        setHeld({ companyId, base: unscoped, scoped: [] })
        setServedFor({ windowKey, accountKey, scope, axis })
        setLoad({ status: 'ready' })
        return
      }
      const again = async (reason: string): Promise<void> => {
        if (attempt >= ROUND_ATTEMPTS) throw new Error(`${windowKey}: ${reason} in ${ROUND_ATTEMPTS} rounds`)
        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, ROUND_PAUSE_MS)
          controller.signal.addEventListener('abort', () => {
            clearTimeout(timer)
            resolve()
          }, { once: true })
        })
        if (!current) return
        return round(retries, attempt + 1)
      }
      const ids = unscoped.dimensions.audiences.map((audience) => audience.id)
      let responses: CreativesResponse[]
      try {
        responses = await Promise.all(
          ids.map((id) =>
            store.read({ ...baseParams, audienceId: id, asOf: unscoped.asOf }, tag, doFetch, signal),
          ),
        )
      } catch (error) {
        if (!(error instanceof CreativesRequestError)) throw error
        if (error.code === 'as_of_out_of_range') {
          if (!current) return
          return again('the base instant aged out')
        }
        if (error.code === 'unknown_audience') {
          if (!current) return
          return again('the audience set changed during the round')
        }
        throw error
      }
      if (!current) return
      // Every response is checked for its contract before its echo, not the base alone. A
      // round that follows one contract throughout is judged by the echo rule, the old one
      // included: its cuts answer a preset the old server still serves. A contract that
      // changes inside the round (a rollback between the base and its cuts) reports the old
      // contract and fetches the round again, once; the shell's fallback alone would not
      // restart it when a preset it keeps is already selected.
      const mixed = responses.some((response) => response.contract !== unscoped.contract)
      if (mixed) {
        if (responses.some((response) => response.contract === 'old')) shell.current.onOldContract?.()
        if (retries > 0) return round(retries - 1)
        throw new Error(`${windowKey}: the server changed contract during the round`)
      }
      if (!responses.every(answers)) throw new Error(`${windowKey}: an audience response answers another window`)
      // A settings save between the base and its cuts would pool buckets judged with two
      // sets of tiers (GRO-186), so different tiers count as a different dataset.
      const oneDataset =
        (unscoped.revision === null ||
          responses.every((response) => response.revision === unscoped.revision && response.asOf === unscoped.asOf)) &&
        responses.every((response) => sameTiers(response, unscoped))
      if (!oneDataset) return again('the dataset changed during the round')
      const oneWindow = responses.every(
        (response) => response.period.from === unscoped.period.from && response.period.to === unscoped.period.to,
      )
      if (!oneWindow && retries > 0) return round(retries - 1)
      setHeld({ companyId, base: unscoped, scoped: responses })
      setServedFor({ windowKey, accountKey, scope, axis })
      setLoad({ status: 'ready' })
    }
    round(1)
      .catch((error: unknown) => {
        if (!current || (error instanceof DOMException && error.name === 'AbortError')) return
        if (error instanceof CreativesRequestError && error.code === 'bad_period') {
          if (period.kind === 'range') shell.current.onPeriodRefused?.({ scope })
          else shell.current.onOldContract?.()
        }
        if (error instanceof CreativesRequestError) {
          setLoad({ status: 'error', message: error.message, code: error.code, accounts: error.accounts })
          return
        }
        setLoad({ status: 'error', message: error instanceof Error ? error.message : String(error) })
      })
    return () => {
      current = false
      controller.abort()
    }
    // `accountKey` and not `accountIds`: the shell builds a fresh array on every render,
    // so depending on the array itself would refetch on every keystroke in a filter. The
    // same set of accounts is the same request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, windowKey, accountKey, axis, attempt, noSelection, scope, reloadKey, fetchImpl])

  // The buckets for the current axis. The base response is identical on every axis; the
  // audience axis waits for every scoped response, because pooling with one missing
  // would break the partition the screen exists to keep.
  const audiencesReady =
    base !== null &&
    (axis !== 'audience' ||
      base.dimensions.audiences.every((audience) =>
        scoped.some((response) => response.scope.audienceId === audience.id),
      ))

  // Read from `base` on every axis: the scope counts are never narrowed by audience (D8).
  const partialScope = base !== null && partialScopeOf(base)
  // The company's tiers as the base this screen renders echoes them (GRO-186), not the
  // shell's: the shell drops its read on a scope change and does not follow this screen's
  // retry, while the screen keeps rendering its retained base.
  const thresholds = useMemo(() => thresholdsOf(base?.verdictBasis), [base])

  const buckets: BucketRow[] = useMemo(() => {
    if (base === null || !audiencesReady) return []
    return axis === 'audience'
      ? poolAudience(base, scoped, thresholds, partialScope)
      : pool(base.creatives, axis, thresholds, partialScope)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, scoped, axis, thresholds, audiencesReady, partialScope])

  const ordered = useMemo(() => orderBuckets(buckets), [buckets])
  const totals = useMemo(
    () => (base !== null ? bucketTotals(base.creatives, partialScope) : null),
    [base, partialScope],
  )

  // The nav count belongs to the shell, which reads the scope itself (GRO-177), so the
  // screen reports nothing: the axis and the filters are view state and must not move a
  // badge. `ready` stays for the no-delivery line and the empty state's way to another
  // period.
  const ready =
    !settling
    && load.status === 'ready'
    && base !== null
    && audiencesReady
    && servedFor !== null
    && servedFor.scope === scope
    && servedFor.windowKey === windowKey
    && servedFor.accountKey === accountKey
  const loading = load.status === 'loading'
  // The displayed rows answer a request this screen is no longer making: keep them on
  // show, but take the drill away until the response catches up (or the error explains
  // why it never will).
  const stale =
    base !== null &&
    (settling ||
      servedFor === null ||
      servedFor.windowKey !== windowKey ||
      servedFor.accountKey !== accountKey)
  // A «Не запущен» creative is no participant: a response holding only those delivered
  // nothing (GRO-187).
  const creativesCount = base !== null ? launched(base.creatives).length : 0
  const hasCreatives = creativesCount > 0
  const nothingDelivered = base !== null && base.accounts.length > 0 && audiencesReady && !hasCreatives && !partialScope
  // What the figures are, read off the accounts that produced them (the creatives
  // screen's rule): the basis sentence says demo only over demo reporting.
  const isDemo = base !== null && base.accounts.length > 0 && base.accounts.every((a) => a.isDemo)

  const nameOf = useCallback(
    (bucket: BucketRow) =>
      bucket.id === UNASSIGNED
        ? axis === 'hypothesis'
          ? t.hypUnassigned
          : t.techUnassigned
        : (labelOf(dimensionList(axis, base?.dimensions ?? { hypotheses: [], techniques: [], messages: [], offers: [], audiences: [] }), bucket.id, lang) ?? bucket.id),
    [axis, base, lang, t],
  )

  const footnote = useMemo(() => {
    if (base === null || !hasCreatives) return null
    const vars = footnoteVariables(
      axis,
      buckets.map((bucket) => ({ id: bucket.id, name: nameOf(bucket), conversions: bucket.metrics.conversions })),
      (base?.dimensions.audiences ?? []).map((audience) => audience.label[lang]),
      thresholds,
      period,
      lang,
    )
    return footnoteText(t, vars)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, buckets, axis, thresholds, windowKey, lang, nameOf, hasCreatives])
  if (noSelection) {
    return (
      <Stack gap="lg" data-testid="hypotheses-screen">
        <Text size="sm" className="grv-muted" data-testid="hypotheses-empty">
          {t.emptySelection}
        </Text>
      </Stack>
    )
  }

  if (load.status === 'error') {
    const retryable = !SHELL_FIXABLE.has(load.code ?? '')
    return (
      <Stack gap="lg" data-testid="hypotheses-screen">
        <Card p="lg" role="alert" data-testid="hypotheses-error" data-code={load.code}>
          <Stack gap="sm">
            <Text size="md" fw={700}>{t.loadFailed}</Text>
            <Text size="sm" className="grv-muted">{errorCopy(load, t)}</Text>
            {retryable && (
              <Group>
                <Button onClick={() => setAttempt((n) => n + 1)}>{t.retry}</Button>
              </Group>
            )}
          </Stack>
        </Card>
      </Stack>
    )
  }

  const axisOptions: Array<{ value: Axis; label: string }> = [
    { value: 'hypothesis', label: t.groupHypothesis },
    { value: 'technique', label: t.groupVisual },
    { value: 'audience', label: t.groupAudience },
  ]

  return (
    <Stack gap="lg" data-testid="hypotheses-screen" aria-busy={loading}>
      {rangeRefused && (
        <Text size="sm" role="status" data-testid="range-refused">{t.periodRangeRefused}</Text>
      )}
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
        <Stack gap={4} style={{ maxWidth: 660 }}>
          <Title order={3} data-testid="hypotheses-title">{t.hypTitle}</Title>
          <Text size="sm" className="grv-muted">{t.hypLede}</Text>
          <Text size="xs" className="grv-muted">{t.hypAxisNote}</Text>
          <Text size="xs" className="grv-muted">{t.hypDrill}</Text>
          {onManageDictionary && (
            <Group>
              <Button variant="subtle" size="compact-sm" onClick={onManageDictionary} data-testid="manage-hypotheses">
                {DICTIONARY_COPY[lang].manage}
              </Button>
            </Group>
          )}
        </Stack>
        <Segmented
          aria-label={t.hypAxisNote}
          value={axis}
          onChange={(value) => onAxisChange(value as Axis)}
          data={axisOptions}
          data-testid="hypotheses-axis"
        />
      </Group>

      {loading && (
        <Group gap="sm" data-testid="hypotheses-loading">
          <Loader size="sm" />
          <Text size="sm">{t.loadingCreatives}</Text>
        </Group>
      )}

      {/* The audience axis keeps its rows hidden until every scoped response has landed:
          a table of the audiences that answered so far would be a comparison missing its
          buckets. The attribute axes keep the previous rows while a refetch runs, the
          creatives screen's rule. */}
      {/* A company with no accounts is the server's empty-company envelope: no comparison
          to draw and no totals to state, the creatives screen's company state. */}
      {base !== null && base.accounts.length === 0 && <EmptyState kind="company" t={t} />}

      {ready && base.accounts.length > 0 && <NoDeliveryLine response={base} t={t} />}
      {base !== null && base.accounts.length > 0 && <ScopeNotices response={base} t={t} />}

      {/* The scope notices take precedence: under a partial scope an empty window is not
          "nothing delivered", it is delivery the figures cannot see yet (D8, C7h). Nothing
          delivered over a complete scope replaces the table: a comparison of no buckets
          has nothing to rank, and the way out is another period. */}
      {nothingDelivered && (
        <EmptyState kind="creatives" t={t} onChangePeriod={ready ? onOpenPeriod : undefined} />
      )}

      {base !== null && base.accounts.length > 0 && audiencesReady && totals !== null && !nothingDelivered && (
        <>
          <HypothesesTable
            t={t}
            lang={lang}
            currency={base.currency}
            thresholds={thresholds}
            dimensions={base.dimensions}
            benchmarks={base.benchmarks}
            axis={axis}
            ordered={ordered}
            totals={totals}
            creativesCount={creativesCount}
            drillDisabled={stale}
            isDemo={isDemo}
            onOpenBucket={onOpenBucket}
          />
          {footnote !== null && <Footnote text={footnote} />}
        </>
      )}
    </Stack>
  )
}

/** Whether two responses were judged with the same tiers; an absent basis reads as the defaults. */
function sameTiers(a: CreativesResponse, b: CreativesResponse): boolean {
  const x = thresholdsOf(a.verdictBasis)
  const y = thresholdsOf(b.verdictBasis)
  return x.directionAt === y.directionAt && x.provisionalAt === y.provisionalAt && x.reliableAt === y.reliableAt
}
